/**
 * Portuguese Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const pt: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Painel',
  'nav.funds': 'Fundos',
  'nav.investments': 'Investimentos',
  'nav.investees': 'Investidas',
  'nav.esg': 'ESG Revisão',
  'nav.compliance': 'Conformidade',
  'nav.reports': 'Relatórios',
  'nav.settings': 'Definições',
  'nav.users': 'Utilizadores',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicadores',
  'nav.taxonomy': 'Taxonomia da UE',
  'nav.filings': 'Declarações',
  'nav.calendar': 'Calendário',
  'nav.submit': 'Submeter dados',
  'nav.submissions': 'As minhas submissões',
  'nav.benchmarks': 'Referências',
  'nav.messages': 'Mensagens',
  'nav.portfolio': 'Carteira',
  'nav.performance': 'Desempenho dos fundos',
  'nav.esgSummary': 'ESG Resumo',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Métricas de impacto',
  'nav.sdg': 'Mapeamento ODS',
  'nav.audit': 'Trilha de auditoria',
  'nav.snapshots': 'Snapshots de dados',
  'nav.knowledgeGraph': 'Grafo de conhecimento',
  'nav.geographic': 'Geográfico',
  'nav.bi': 'BI Estúdio',
  'nav.ai': 'Assistente IA',
  'nav.tasks': 'Tarefas',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Monitor regulatório',

  // Common Actions
  'action.create': 'Criar',
  'action.edit': 'Editar',
  'action.delete': 'Eliminar',
  'action.save': 'Guardar',
  'action.cancel': 'Cancelar',
  'action.submit': 'Submeter',
  'action.approve': 'Aprovar',
  'action.reject': 'Rejeitar',
  'action.download': 'Descarregar',
  'action.export': 'Exportar',
  'action.import': 'Importar',
  'action.filter': 'Filtrar',
  'action.search': 'Pesquisar',
  'action.refresh': 'Atualizar',
  'action.close': 'Fechar',
  'action.back': 'Voltar',
  'action.next': 'Seguinte',
  'action.previous': 'Anterior',
  'action.viewAll': 'Ver tudo',
  'action.viewDetails': 'Ver detalhes',
  'action.generate': 'Gerar',
  'action.signOut': 'Terminar sessão',
  'action.addNew': 'Adicionar novo',
  'action.invite': 'Convidar',

  // Statuses
  'status.draft': 'Rascunho',
  'status.submitted': 'Submetido',
  'status.validated': 'Validado',
  'status.approved': 'Aprovado',
  'status.rejected': 'Rejeitado',
  'status.pending': 'Pendente',
  'status.inProgress': 'Em curso',
  'status.overdue': 'Em atraso',
  'status.compliant': 'Conforme',
  'status.filed': 'Declarado',
  'status.active': 'Ativo',
  'status.inactive': 'Inativo',
  'status.closed': 'Encerrado',
  'status.fundraising': 'Captação de recursos',
  'status.investing': 'Investimento',
  'status.harvesting': 'Colheita',
  'status.validating': 'A validar',
  'status.review': 'Em revisão',
  'status.monitoring': 'Monitoramento',
  'status.exiting': 'Saída',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Due Diligence',
  'status.disbursed': 'Desembolsado',
  'status.restructured': 'Reestruturado',
  'status.matured': 'Vencido',
  'status.written_off': 'Baixado',
  'status.exited': 'Saído',
  'status.incomplete': 'Incompleto',
  'status.open': 'Aberto',
  'status.in_progress': 'Em andamento',
  'status.completed': 'Concluído',
  'status.cancelled': 'Cancelado',
  'status.accepted': 'Aceite',
  'status.failed': 'Falhado',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Visão geral da carteira',
  'dashboard.complianceStatus': 'Estado de conformidade',
  'dashboard.collectionProgress': 'Progresso da recolha',
  'dashboard.activityFeed': 'Atividade',
  'dashboard.quickActions': 'Ações rápidas',
  'dashboard.investmentPipeline': 'Pipeline de investimento',
  'dashboard.totalFunds': 'Total de fundos',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Investimentos ativos',
  'dashboard.totalInvestees': 'Investidas',
  'dashboard.recentActivity': 'Atividade recente',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total comprometido',
  'lp.capitalCalled': 'Capital chamado',
  'lp.distributed': 'Distribuído',
  'lp.netPosition': 'Posição líquida',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Desempenho dos fundos',
  'lp.esgSummary': 'ESG Resumo',
  'lp.reportLibrary': 'Biblioteca de relatórios',
  'lp.taxonomyAligned': 'Alinhado com a taxonomia',
  'lp.paiCoverage': 'PAI Cobertura',
  'lp.portfolioPerformance': 'Desempenho da carteira LP',

  // Fund Labels
  'fund.name': 'Nome do fundo',
  'fund.code': 'Código do fundo',
  'fund.vintage': 'Ano de constituição',
  'fund.size': 'Dimensão do fundo',
  'fund.targetSize': 'Dimensão alvo',
  'fund.currency': 'Moeda',
  'fund.strategy': 'Estratégia',
  'fund.status': 'Estado',
  'fund.sfdrCategory': 'SFDR Categoria',
  'fund.geographyFocus': 'Foco geográfico',
  'fund.sectorFocus': 'Foco setorial',

  // Form Labels
  'form.name': 'Nome',
  'form.email': 'E-mail',
  'form.country': 'País',
  'form.amount': 'Montante',
  'form.currency': 'Moeda',
  'form.date': 'Data',
  'form.description': 'Descrição',
  'form.notes': 'Notas',
  'form.type': 'Tipo',
  'form.period': 'Período',
  'form.year': 'Ano',
  'form.required': 'Obrigatório',
  'form.optional': 'Opcional',

  // Table Headers
  'table.fund': 'Fundo',
  'table.commitment': 'Compromisso',
  'table.called': 'Chamado',
  'table.distributed': 'Distribuído',
  'table.tvpi': 'TVPI',
  'table.status': 'Estado',
  'table.regulation': 'Regulamento',
  'table.filingType': 'Tipo de declaração',
  'table.reportingPeriod': 'Período',
  'table.createdAt': 'Criado',
  'table.actions': 'Ações',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomia da UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Completude dos dados',
  'compliance.nextDeadline': 'Próximo prazo',

  // Reports
  'report.quarterly': 'Relatório trimestral',
  'report.annual': 'Relatório anual',
  'report.paiStatement': 'PAI Declaração',
  'report.taxonomyReport': 'Relatório de taxonomia',
  'report.generatedAt': 'Gerado',
  'report.version': 'Versão',

  // General
  'general.loading': 'A carregar...',
  'general.noData': 'Sem dados disponíveis',
  'general.error': 'Ocorreu um erro',
  'general.retry': 'Tentar novamente',
  'general.total': 'Total',
  'general.page': 'Página',
  'general.of': 'de',
  'general.showing': 'A mostrar',
  'general.results': 'resultados',
  'general.language': 'Idioma',
  'general.english': 'Inglês',
  'general.french': 'Francês',

  // Tour
  'tour.takeTour': 'Fazer o tour',
  'tour.skip': 'Pular',
  'tour.next': 'Próximo',
  'tour.prev': 'Anterior',
  'tour.finish': 'Concluir',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navegação',
  'tour.sidebarContent': 'Use a barra lateral para navegar entre gestão de carteira, recolha de dados ESG, conformidade, relatórios e ferramentas de inteligência.',
  'tour.kpisTitle': 'Visão geral da carteira',
  'tour.kpisContent': 'Estes cartões mostram a sua carteira de relance — fundos totais, AUM, investimentos ativos e número de investidas.',
  'tour.actionsTitle': 'Ações rápidas',
  'tour.actionsContent': 'Crie fundos, adicione investimentos, convide investidas e gere relatórios com um clique.',
  'tour.languageTitle': 'Idioma',
  'tour.languageContent': 'Alterne entre 24 línguas oficiais da UE. Toda a interface é traduzida instantaneamente.',
  'tour.aiTitle': 'Assistente IA',
  'tour.aiContent': 'Faça perguntas sobre a sua carteira a qualquer momento. O assistente IA tem contexto completo sobre os seus fundos e investimentos.',
  'action.createFund': 'Criar fundo',
  'action.editFund': 'Editar fundo',
  'action.addInvestment': 'Adicionar investimento',
  'action.addInvestee': 'Adicionar investida',
  'action.editInvestee': 'Editar investida',
  'action.createTemplate': 'Criar modelo',
  'action.createSubmission': 'Criar submissão',
  'action.createFiling': 'Criar declaração',
  'action.generateReport': 'Gerar relatório',
  'action.inviteUser': 'Convidar utilizador',
  'action.sendInvitation': 'Enviar convite',
  'action.saveChanges': 'Guardar alterações',
  'form.fundName': 'Nome do fundo',
  'form.fundCode': 'Código do fundo',
  'form.sfdrCategory': 'Categoria SFDR',
  'form.baseCurrency': 'Moeda base',
  'form.targetSize': 'Dimensão alvo',
  'form.vintageYear': 'Ano de constituição',
  'form.geographyFocus': 'Foco geográfico',
  'form.sectorFocus': 'Foco setorial',
  'form.investmentCode': 'Código de investimento',
  'form.instrumentType': 'Tipo de instrumento',
  'form.maturityDate': 'Data de vencimento',
  'form.sector': 'Setor',
  'form.countryCode': 'Código do país',
  'form.entityType': 'Tipo de entidade',
  'form.region': 'Região',
  'form.staffCount': 'Número de funcionários',
  'form.borrowers': 'Mutuários',
  'form.contactName': 'Nome de contacto',
  'form.contactEmail': 'E-mail de contacto',
  'form.templateName': 'Nome do modelo',
  'form.templateType': 'Tipo de modelo',
  'form.indicators': 'Indicadores',
  'form.reportingPeriod': 'Período de reporte',
  'form.reportingYear': 'Ano de reporte',
  'form.regulation': 'Regulamento',
  'form.filingType': 'Tipo de declaração',
  'form.reportType': 'Tipo de relatório',
  'form.emailAddress': 'Endereço de e-mail',
  'form.role': 'Função',
  'form.selectFund': 'Selecionar fundo...',
  'form.selectInvestee': 'Selecionar investida...',
  'table.code': 'Código',
  'table.investee': 'Investida',
  'table.instrument': 'Instrumento',
  'table.amount': 'Montante',
  'table.currency': 'Moeda',
  'table.maturity': 'Vencimento',
  'table.name': 'Nome',
  'table.country': 'País',
  'table.type': 'Tipo',
  'table.period': 'Período',
  'table.completion': 'Conclusão',
  'table.submitted': 'Submetido',
  'table.version': 'Versão',
  'table.indicators': 'Indicadores',
  'table.dueDate': 'Data limite',
  'table.generated': 'Gerado',
  'table.email': 'E-mail',
  'table.role': 'Função',
  'error.createFailed': 'Falha ao criar. Tente novamente.',
  'error.updateFailed': 'Falha ao atualizar. Tente novamente.',
  'error.loadFailed': 'Falha ao carregar dados.',

  // Sidebar Sections
  'sections.portfolio': 'PORTFÓLIO',
  'sections.dataCollection': 'COLETA DE DADOS',
  'sections.intelligence': 'INTELIGÊNCIA',
  'sections.ecosystem': 'ECOSSISTEMA',
  'sections.admin': 'ADMINISTRAÇÃO',
  'sections.other': 'OUTROS',

  // Page Titles
  'pages.messagesTitle': 'Mensagens',
  'pages.taxonomyTitle': 'Taxonomia UE',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Grafo de Conhecimento',
  'pages.benchmarksTitle': 'Benchmarks',
  'pages.mcpTitle': 'Integração MCP',
  'pages.tasksTitle': 'Tarefas',
  'pages.submitDataTitle': 'Enviar dados ESG',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarks em breve',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Taxonomia UE',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Outro',
  'labels.critical': 'Crítico',
  'labels.high': 'Alto',
  'labels.medium': 'Médio',
  'labels.low': 'Baixo',
};
